<ng-container [ngSwitch]="view">

    <!-- Not on Connect: what it is, what it costs, how it switches on -->
    <section *ngSwitchCase="'none'" class="connect-intro">
        <p class="lead">Submit claims, check eligibility and get paid by medical aids, from the patient file.</p>

        <ul class="features">
            <li *ngFor="let feature of features">
                <mat-icon>{{ feature.icon }}</mat-icon>
                <span>{{ feature.text }}</span>
            </li>
        </ul>

        <div class="pricing">
            <mat-icon>sell</mat-icon>
            <div>
                <strong>1.1% of the money recovered</strong>
                <span class="muted">from medical aids and patients, with a R2,500 monthly minimum.</span>
            </div>
        </div>

        <h4>How it switches on</h4>
        <ol class="steps">
            <li *ngFor="let step of steps; let i = index" [class.done]="step.done">
                <span class="step-mark">
                    <mat-icon *ngIf="step.done">check</mat-icon>
                    <ng-container *ngIf="!step.done">{{ i + 1 }}</ng-container>
                </span>
                <div>
                    <strong>{{ step.label }}</strong>
                    <span class="muted">{{ step.done ? 'Done.' : step.detail }}</span>
                </div>
            </li>
        </ol>

        <div class="actions">
            <button mat-raised-button color="primary" (click)="enable.emit()">Enable Connect</button>
        </div>
    </section>

    <!-- Connect is setting the practice up -->
    <section *ngSwitchCase="'pending'" class="connect-status">
        <mat-spinner diameter="40"></mat-spinner>
        <h4>Setting up MedicAI Connect</h4>
        <p class="muted">
            Connect is creating your practice and the logins for the members you chose. This usually takes under a
            minute. You can leave this page; it updates by itself.
        </p>
        <div *ngIf="slow" class="notice warn">
            <mat-icon>schedule</mat-icon>
            <span>This is taking longer than usual. You can send the setup again.</span>
            <button mat-stroked-button color="primary" (click)="retry()" [disabled]="retrying">
                {{ retrying ? 'Sending...' : 'Try again' }}
            </button>
        </div>
    </section>

    <!-- Connect could not finish -->
    <section *ngSwitchCase="'failed'" class="connect-status">
        <mat-icon class="status-icon error">error_outline</mat-icon>
        <h4>Connect could not finish setting up this practice</h4>
        <p class="reason">{{ group?.connectProvisionError || 'Something went wrong on MedicAI Connect.' }}</p>
        <div class="actions">
            <button mat-button (click)="enable.emit()">Change who gets access</button>
            <button mat-raised-button color="primary" (click)="retry()" [disabled]="retrying">
                <mat-spinner *ngIf="retrying" diameter="18" class="button-spinner"></mat-spinner>
                {{ retrying ? 'Sending...' : 'Try again' }}
            </button>
        </div>
    </section>

    <!-- Set up, but Connect could not be read just now -->
    <section *ngSwitchCase="'unreachable'" class="connect-status">
        <mat-icon class="status-icon error">cloud_off</mat-icon>
        <h4>MedicAI Connect could not be reached</h4>
        <p class="reason">This practice is set up on Connect, but its settings could not be loaded just now.</p>
        <div class="actions">
            <button mat-raised-button color="primary" (click)="load()" [disabled]="loading">Try again</button>
        </div>
    </section>

    <!-- On Connect -->
    <section *ngSwitchCase="'provisioned'" class="connect-on">
        <div class="on-header">
            <span class="badge-on"><mat-icon>check_circle</mat-icon> Connect is on</span>
            <a *ngIf="settings?.connectOrigin" mat-raised-button color="primary" [href]="openConnectUrl" target="_blank"
                rel="noopener">
                Open Connect <mat-icon iconPositionEnd>open_in_new</mat-icon>
            </a>
        </div>

        <h4>Patients your bureau sees</h4>
        <div class="sharing">
            <p>
                Patients sent with a billing batch are kept up to date in Connect as they change. Medical aid details and
                authorisations your bureau changes in Connect are kept.
            </p>
            <mat-slide-toggle *ngIf="canShare; else sharingState" [(ngModel)]="shareAllPatients" color="primary"
                [disabled]="saving" (change)="saveSharing()">Share all patients with my billing bureau</mat-slide-toggle>
            <ng-template #sharingState>
                <p>{{ shareAllPatients ? 'Every patient is shared with your billing bureau.' : 'Only billed patients are shared.' }}</p>
            </ng-template>
        </div>

        <div *ngIf="loading && !settings" class="flex-center p-t-20"><mat-spinner diameter="32"></mat-spinner></div>

        <div *ngIf="notLinked" class="notice">
            <mat-icon>lock</mat-icon>
            <span>Your account does not have Connect access. Ask the practice owner to give you access here.</span>
        </div>

        <ng-container *ngIf="settings && !notLinked">
            <div class="access-heading">
                <h4>Who has access</h4>
                <button *ngIf="settings.canManage && addable.length" mat-icon-button color="primary" (click)="openAdd()"
                    matTooltip="Give a practice member Connect access" aria-label="Give a practice member Connect access">
                    <mat-icon>person_add</mat-icon>
                </button>
            </div>
            <mat-form-field class="full-width" appearance="outline">
                <mat-label>Connect users</mat-label>
                <mat-chip-grid #accessGrid aria-label="Practice members with Connect access">
                    <mat-chip-row *ngFor="let member of members" [appTouchEdit]="manageable(member)"
                        (dblclick)="openEdit(member)" [matTooltip]="member.email">
                        {{ member.name }} ({{ roleLabel(member) }})
                        <button matChipRemove *ngIf="manageable(member)" (click)="removeMember(member)" [disabled]="saving"
                            [attr.aria-label]="'Remove Connect access for ' + member.name">
                            <mat-icon>cancel</mat-icon>
                        </button>
                    </mat-chip-row>
                </mat-chip-grid>
                <input [matChipInputFor]="accessGrid" disabled />
                <mat-hint *ngIf="settings.canManage">Double click or tap and hold a member to change their Connect role</mat-hint>
            </mat-form-field>

            <h4>Who pays for Connect</h4>
            <div class="billing">
                <p [ngSwitch]="settings.billing.arrangement">
                    <ng-container *ngSwitchCase="'shared_clinical'">
                        Your subscription card<span *ngIf="settings.billing.card"> ({{ settings.billing.card.brand | titlecase }}
                            ending {{ settings.billing.card.last4 }})</span>, charged on day {{ settings.billing.billingDayOfMonth }} of
                        each month.
                    </ng-container>
                    <ng-container *ngSwitchCase="'own_card'">
                        A card added in MedicAI Connect<span *ngIf="settings.billing.card"> ({{ settings.billing.card.brand | titlecase }}
                            ending {{ settings.billing.card.last4 }})</span>.
                    </ng-container>
                    <ng-container *ngSwitchCase="'bureau'">Your billing bureau pays for Connect.</ng-container>
                    <ng-container *ngSwitchDefault>No card is set up for Connect yet.</ng-container>
                </p>

                <ng-container *ngIf="settings.canManage && settings.billing.arrangement !== 'bureau'">
                    <mat-slide-toggle [(ngModel)]="billingShare" color="primary">Use my subscription card for Connect</mat-slide-toggle>
                    <div class="billing-edit">
                        <mat-form-field *ngIf="billingShare" appearance="outline" class="day-field">
                            <mat-label>Billing day of month</mat-label>
                            <input matInput type="number" min="1" max="28" step="1" [(ngModel)]="billingDay" />
                            <mat-hint>1 to 28</mat-hint>
                        </mat-form-field>
                        <button mat-stroked-button color="primary" (click)="saveBilling()"
                            [disabled]="saving || (billingShare && (billingDay < 1 || billingDay > 28))">Save</button>
                    </div>
                </ng-container>
            </div>
        </ng-container>
    </section>

</ng-container>

<ng-template #memberDialog>
    <h1 mat-dialog-title>{{ editing?.isNew ? 'Give Connect access' : 'Connect role for ' + editing?.name }}</h1>
    <div mat-dialog-content *ngIf="editing" class="member-dialog">
        <mat-form-field *ngIf="editing.isNew" appearance="outline" class="full-width">
            <mat-label>Practice member</mat-label>
            <mat-select [(ngModel)]="editing.userId">
                <mat-option *ngFor="let user of addable" [value]="user.userId">{{ userLabel(user) }}</mat-option>
            </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
            <mat-label>Connect role</mat-label>
            <mat-select [(ngModel)]="editing.role">
                <mat-option *ngFor="let option of roleOptions" [value]="option.value">{{ option.label }}</mat-option>
            </mat-select>
        </mat-form-field>
    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-button mat-dialog-close>Cancel</button>
        <button mat-raised-button color="primary" (click)="saveEditing()" [disabled]="saving || !editing?.userId">
            {{ saving ? 'Saving...' : 'Save' }}
        </button>
    </div>
</ng-template>
